// groupActivityItemsByDay.js

import { toActivityTimestampMs } from 'node/activity/utils/buildActivityFeedItems';

/**
 * Milliseconds for an activity feed row.
 *
 * @param {object|null|undefined} item
 * @returns {number}
 */
export function getActivityItemTimestampMs(item) {
  if (Number.isFinite(item?.at) && item.at > 0) return item.at;

  return toActivityTimestampMs(item?.event?.occurredAt)
    || toActivityTimestampMs(item?.event?.timestamp)
    || toActivityTimestampMs(item?.event?.dateCreated)
    || toActivityTimestampMs(item?.comment?.dateCreated)
    || 0;
}

/**
 * Local calendar day key (YYYY-MM-DD).
 *
 * @param {number} ms
 * @returns {string}
 */
export function getActivityDayKey(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Timeline header for a day section.
 *
 * @param {number} ms
 * @returns {string}
 */
export function formatActivityDayLabel(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).toUpperCase();
}

/**
 * Groups a time-ordered feed into consecutive same-day sections.
 *
 * @param {object[]} items
 * @returns {Array<{
 *   key: string,
 *   dayKey: string,
 *   dateLabel: string,
 *   items: object[],
 * }>}
 */
export function groupActivityItemsByDay(items) {
  const sections = [];

  (Array.isArray(items) ? items : []).forEach((item, index) => {
    if (!item) return;

    const ms = getActivityItemTimestampMs(item);
    const dayKey = getActivityDayKey(ms);
    const last = sections[sections.length - 1];

    if (last && last.dayKey === dayKey) {
      last.items.push(item);
      return;
    }

    sections.push({
      key: dayKey ? `${dayKey}:${item.key}` : `undated:${item.key || index}`,
      dayKey,
      dateLabel: formatActivityDayLabel(ms),
      items: [item],
    });
  });

  return sections;
}
